import { recipeInputSchema, recipeUpdateSchema, type Page, type Recipe } from "@cafegestor/shared";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  Input,
  PageTitle,
  Select,
  Spinner,
  Table,
} from "@/components/ui";
import { useCan } from "@/app/me";
import { apiFetch } from "@/lib/api";
import { Pager, SaveError, StatusBadge, SearchInput } from "../cadastros/widgets";
import { qty, unitsOf, useDocAction, useProductDetail } from "../estoque/lib";
import { ProductPicker } from "../estoque/pickers";

type ItemDraft = {
  key: number;
  productId: string;
  label: string;
  unitId: string;
  quantity: string;
};

export function FichasPage() {
  const canEdit = useCan()("producao.ficha_tecnica.gerenciar");
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const [editing, setEditing] = useState<string | "new" | null>(null);
  const params = new URLSearchParams({ status: "all", limit: "50", offset: String(offset) });
  if (q.trim()) params.set("q", q.trim());
  const list = useQuery({
    queryKey: ["producao", "fichas", params.toString()],
    queryFn: () =>
      apiFetch<Page<Recipe & { itemCount: number }>>(`/producao/fichas?${params.toString()}`),
    placeholderData: (p) => p,
  });

  return (
    <div className="space-y-6">
      <PageTitle
        title="Fichas técnicas"
        description="O que se consome para produzir: café cru, embalagens, etiquetas, caixas. A ordem de produção calcula o consumo pela ficha."
        actions={
          canEdit && (
            <Button onClick={() => setEditing("new")} disabled={editing === "new"}>
              Nova ficha
            </Button>
          )
        }
      />
      {editing && (
        <RecipeEditor
          key={editing}
          id={editing === "new" ? null : editing}
          canEdit={canEdit}
          onClose={() => setEditing(null)}
        />
      )}
      <Card>
        <SearchInput
          placeholder="Buscar por produto ou nome da ficha"
          value={q}
          onChange={(v) => {
            setQ(v);
            setOffset(0);
          }}
        />
        {list.isPending ? (
          <Spinner />
        ) : (
          <>
            <Table
              head={["Produto", "Ficha", "Rende", "Componentes", "Perda prevista", "Situação", ""]}
            >
              {list.data?.items.map((r) => (
                <tr key={r.id}>
                  <td className="px-3 py-2">
                    <span className="font-mono text-xs">{r.productCode}</span> {r.productName}
                  </td>
                  <td className="px-3 py-2">
                    {r.name} {r.isDefault && <Badge tone="ok">padrão</Badge>}
                  </td>
                  <td className="px-3 py-2">{qty(r.outputQuantity, r.outputUnitCode)}</td>
                  <td className="px-3 py-2">{r.itemCount}</td>
                  <td className="text-right tabular-nums px-3 py-2">
                    {r.expectedLossPercent ? `${Number(r.expectedLossPercent)}%` : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge status={r.status} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(r.id)}>
                      {canEdit ? "Editar" : "Ver"}
                    </Button>
                  </td>
                </tr>
              ))}
            </Table>
            <Pager total={list.data?.total ?? 0} offset={offset} onOffset={setOffset} />
          </>
        )}
      </Card>
    </div>
  );
}

function RecipeEditor({
  id,
  canEdit,
  onClose,
}: {
  id: string | null;
  canEdit: boolean;
  onClose: () => void;
}) {
  const recipe = useQuery({
    queryKey: ["producao", "fichas", "detalhe", id],
    queryFn: () => apiFetch<Recipe>(`/producao/fichas/${id}`),
    enabled: id !== null,
  });
  if (id && !recipe.data) return <Spinner />;
  return <RecipeForm recipe={recipe.data ?? null} canEdit={canEdit} onClose={onClose} />;
}

function RecipeForm({
  recipe,
  canEdit,
  onClose,
}: {
  recipe: Recipe | null;
  canEdit: boolean;
  onClose: () => void;
}) {
  const [productId, setProductId] = useState(recipe?.productId ?? "");
  const [productLabel, setProductLabel] = useState(
    recipe ? `${recipe.productCode} — ${recipe.productName}` : "",
  );
  const [name, setName] = useState(recipe?.name ?? "Padrão");
  const [outputUnitId, setOutputUnitId] = useState(recipe?.outputUnitId ?? "");
  const [outputQuantity, setOutputQuantity] = useState(
    recipe ? qty(recipe.outputQuantity).replace(/\./g, "") : "1",
  );
  const [loss, setLoss] = useState(
    recipe?.expectedLossPercent ? String(Number(recipe.expectedLossPercent)) : "",
  );
  const [isDefault, setIsDefault] = useState(recipe?.isDefault ?? true);
  const [status, setStatus] = useState<"active" | "inactive">(recipe?.status ?? "active");
  const [notes, setNotes] = useState(recipe?.notes ?? "");
  const [items, setItems] = useState<ItemDraft[]>(
    recipe?.items?.map((i, n) => ({
      key: n + 1,
      productId: i.productId,
      label: `${i.productCode} — ${i.productName}`,
      unitId: i.unitId,
      quantity: qty(i.quantity).replace(/\./g, ""),
    })) ?? [{ key: 1, productId: "", label: "", unitId: "", quantity: "" }],
  );
  const [formError, setFormError] = useState<string | null>(null);
  const product = useProductDetail(productId);
  const outUnits = unitsOf(product.data);
  const save = useDocAction<unknown, Recipe>({
    path: () => (recipe ? `/producao/fichas/${recipe.id}` : "/producao/fichas"),
    method: recipe ? "PUT" : "POST",
    invalidate: [["producao", "fichas"]],
    onDone: onClose,
    successMessage: () =>
      recipe ? "Ficha técnica atualizada com sucesso." : "Ficha técnica criada com sucesso.",
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const base = {
      name,
      outputUnitId: outputUnitId || outUnits[0]?.id,
      outputQuantity,
      expectedLossPercent: loss,
      notes,
      isDefault,
      status,
      items: items
        .filter((i) => i.productId)
        .map((i) => ({ productId: i.productId, unitId: i.unitId, quantity: i.quantity })),
    };
    const parsed = recipe
      ? recipeUpdateSchema.safeParse({ ...base, version: recipe.version })
      : recipeInputSchema.safeParse({ ...base, productId });
    if (!parsed.success) return setFormError(parsed.error.issues[0]?.message ?? "Dados inválidos");
    setFormError(null);
    save.mutate(parsed.data);
  }

  return (
    <Card title={recipe ? `Ficha "${recipe.name}" — ${recipe.productName}` : "Nova ficha técnica"}>
      <form onSubmit={submit} className="space-y-4">
        <fieldset disabled={!canEdit} className="grid gap-4 sm:grid-cols-4">
          <div className="sm:col-span-2">
            <Field label="Produto fabricado">
              <ProductPicker
                value={productId}
                label={productLabel}
                disabled={Boolean(recipe)}
                onChange={(pid, l) => {
                  setProductId(pid);
                  setProductLabel(l);
                  setOutputUnitId("");
                }}
              />
            </Field>
          </div>
          <Field label="Nome da ficha" required>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Situação">
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value as "active" | "inactive")}
            >
              <option value="active">Ativa</option>
              <option value="inactive">Inativa</option>
            </Select>
          </Field>
          <Field label="Rende (quantidade)">
            <Input
              inputMode="decimal"
              value={outputQuantity}
              onChange={(e) => setOutputQuantity(e.target.value)}
            />
          </Field>
          <Field label="Unidade do rendimento">
            <Select
              value={outputUnitId || outUnits[0]?.id || ""}
              onChange={(e) => setOutputUnitId(e.target.value)}
            >
              {outUnits.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.code}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Perda prevista (%)" hint="Ex.: perda de torra">
            <Input inputMode="decimal" value={loss} onChange={(e) => setLoss(e.target.value)} />
          </Field>
          <label className="inline-flex items-center gap-2 pt-6 text-sm">
            <input
              type="checkbox"
              className="size-4 accent-primary"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
            />
            Ficha padrão do produto
          </label>
          <div className="sm:col-span-4">
            <Field label="Observações">
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
            </Field>
          </div>
        </fieldset>

        <h3 className="font-semibold">Componentes (para o rendimento acima)</h3>
        {items.map((it) => (
          <ComponentLine
            key={it.key}
            item={it}
            disabled={!canEdit}
            onChange={(v) => setItems((xs) => xs.map((x) => (x.key === v.key ? v : x)))}
            onRemove={() => setItems((xs) => xs.filter((x) => x.key !== it.key))}
          />
        ))}
        {canEdit && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() =>
              setItems([
                ...items,
                {
                  key: Math.max(0, ...items.map((x) => x.key)) + 1,
                  productId: "",
                  label: "",
                  unitId: "",
                  quantity: "",
                },
              ])
            }
          >
            Adicionar componente
          </Button>
        )}
        {canEdit && (
          <div className="flex gap-2">
            <Button type="submit" disabled={save.isPending}>
              Salvar ficha
            </Button>
            <Button variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
          </div>
        )}
        {formError && <Alert tone="error">{formError}</Alert>}
        <SaveError error={save.error} />
      </form>
    </Card>
  );
}

function ComponentLine({
  item,
  disabled,
  onChange,
  onRemove,
}: {
  item: ItemDraft;
  disabled: boolean;
  onChange: (v: ItemDraft) => void;
  onRemove: () => void;
}) {
  const product = useProductDetail(item.productId);
  const units = unitsOf(product.data);
  const firstUnit = units[0]?.id;
  // escolhido o produto, assume a unidade-base até o usuário trocar
  useEffect(() => {
    if (!item.unitId && firstUnit) onChange({ ...item, unitId: firstUnit });
  }, [item, firstUnit, onChange]);
  return (
    <div className="grid gap-3 rounded-md border p-3 sm:grid-cols-12 sm:items-end">
      <div className="sm:col-span-6">
        <Field label="Componente">
          <ProductPicker
            value={item.productId}
            label={item.label}
            disabled={disabled}
            onChange={(id, l) => onChange({ ...item, productId: id, label: l, unitId: "" })}
          />
        </Field>
      </div>
      <div className="sm:col-span-2">
        <Field label="Quantidade">
          <Input
            inputMode="decimal"
            disabled={disabled}
            value={item.quantity}
            onChange={(e) =>
              onChange({
                ...item,
                quantity: e.target.value,
                unitId: item.unitId || units[0]?.id || "",
              })
            }
          />
        </Field>
      </div>
      <div className="sm:col-span-2">
        <Field label="Unidade">
          <Select
            disabled={disabled}
            value={item.unitId || units[0]?.id || ""}
            onChange={(e) => onChange({ ...item, unitId: e.target.value })}
          >
            {units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.code}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <div className="sm:col-span-2 text-right">
        {!disabled && (
          <Button size="sm" variant="ghost" onClick={onRemove}>
            Remover
          </Button>
        )}
      </div>
    </div>
  );
}
